import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FaEllipsisVertical, FaFolderMinus } from 'react-icons/fa6';
import { useCloseAlbum } from '../../api/queryHooks/pic';
import { useClickOutside } from '../../hooks/useClickOutside';
import ModalWindow from '../common/ModalWindow';
import type { Album } from '../../api/types';

interface AlbumCloseMenuProps {
  album: Album;
  /** Lets the card lift itself above its neighbours while the menu is open. */
  onOpenChange?: (open: boolean) => void;
  /** After a successful close — the album page leaves the album it just closed. */
  onClosed?: () => void;
}

/**
 * The ⋮ menu for an album the buyer opened (album card and album page header):
 * "Close album", behind a confirmation (reopening takes the album code again).
 * Closing drops the buyer's assoc and their notifications on the album
 * (pic-service).
 */
const AlbumCloseMenu = ({ album, onOpenChange, onClosed }: AlbumCloseMenuProps) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpenState] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const { mutate: closeAlbum, isPending } = useCloseAlbum();

  const setMenuOpen = useCallback((open: boolean) => {
    setMenuOpenState(open);
    onOpenChange?.(open);
  }, [onOpenChange]);
  const ref = useClickOutside<HTMLDivElement>(useCallback(() => setMenuOpen(false), [setMenuOpen]));

  const title = album.title || t('albumlist.untitled');

  return (
    <>
      <div className={`dropdown is-right${menuOpen ? ' is-active' : ''}`} ref={ref}>
        <div className="dropdown-trigger">
          <button
            type="button"
            className="album-action-btn"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={t('albumlist.more_actions')}
            title={t('albumlist.more_actions')}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <FaEllipsisVertical />
          </button>
        </div>
        {menuOpen && (
          <div className="dropdown-menu" role="menu">
            <div className="dropdown-content">
              <button
                type="button"
                role="menuitem"
                className="dropdown-item album-menu-item"
                onClick={() => { setMenuOpen(false); setConfirming(true); }}
              >
                <FaFolderMinus className="mr-2" />
                {t('albumlist.close_album')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Portalled: a transformed ancestor (any card styling with a transform)
          would become the containing block of the modal's position: fixed. */}
      {createPortal(
        <ModalWindow
          isOpen={confirming}
          onClose={() => setConfirming(false)}
          title={t('albumlist.close_title')}
          size="small"
          mobileBottomSheet
          buttons={[
            { label: t('common.cancel'), onClick: () => setConfirming(false), className: 'button' },
            {
              label: t('albumlist.close_confirm'),
              className: `button is-danger${isPending ? ' is-loading' : ''}`,
              disabled: isPending,
              onClick: () => closeAlbum(album.id, {
                onSuccess: () => {
                  setConfirming(false);
                  onClosed?.();
                },
              }),
            },
          ]}
        >
          <p className="mb-3">{t('albumlist.close_body', { title })}</p>
          <p className="has-text-weight-semibold">{t('albumlist.close_reopen_hint')}</p>
        </ModalWindow>,
        document.body,
      )}
    </>
  );
};

export default AlbumCloseMenu;
